<script setup>
/**
 * Gravador de voz do reply box.
 * Pede permissão do mic, converte p/ MP3 e avisa se falhar (antes falhava em silêncio).
 */
import getUuid from 'widget/helpers/uuid';
import { ref, onMounted, onUnmounted, defineEmits, defineExpose } from 'vue';
import WaveSurfer from 'wavesurfer.js';
import RecordPlugin from 'wavesurfer.js/dist/plugins/record.js';
import { format, intervalToDuration } from 'date-fns';
import { convertAudio } from './utils/mp3ConversionUtils';
import {
  micErrorMessage,
  ensureMicrophoneAccess,
  fallbackRecordedFile,
} from './utils/audioRecorderHelpers';

const props = defineProps({
  audioRecordFormat: { type: String, required: true },
});

const emit = defineEmits([
  'recorderProgressChanged',
  'finishRecord',
  'recorderError',
  'pause',
  'play',
]);

const waveformContainer = ref(null);
const wavesurfer = ref(null);
const record = ref(null);
const isRecording = ref(false);
const isPlaying = ref(false);
const hasRecording = ref(false);
/** Invalida convert após cancelar (race com record-end). */
const cancelGeneration = ref(0);

const formatTimeProgress = time => {
  const duration = intervalToDuration({ start: 0, end: time });
  return format(
    new Date(0, 0, 0, 0, duration.minutes, duration.seconds),
    'mm:ss'
  );
};

const fail = (error, rawBlob = null) => {
  isRecording.value = false;
  // Fallback: ainda envia o webm/ogg original se a conversão MP3 quebrar.
  if (rawBlob && rawBlob.size > 0 && cancelGeneration.value === 0) {
    emit('finishRecord', fallbackRecordedFile(rawBlob, getUuid()));
    return;
  }
  emit('recorderError', micErrorMessage(error));
  emit('finishRecord', null);
};

const initWaveSurfer = () => {
  wavesurfer.value = WaveSurfer.create({
    container: waveformContainer.value,
    waveColor: '#1F93FF',
    progressColor: '#6E6F73',
    height: 100,
    barWidth: 2,
    barGap: 1,
    barRadius: 2,
    plugins: [
      RecordPlugin.create({
        scrollingWaveform: true,
        renderRecordedAudio: false,
        mimeType: 'audio/webm;codecs=opus',
        audioBitsPerSecond: 128000,
      }),
    ],
  });

  wavesurfer.value.on('pause', () => emit('pause'));
  wavesurfer.value.on('play', () => emit('play'));
  record.value = wavesurfer.value.plugins[0];
  wavesurfer.value.on('finish', () => {
    isPlaying.value = false;
  });

  record.value.on('record-end', async blob => {
    const idAtStart = cancelGeneration.value;
    const audioUrl = URL.createObjectURL(blob);
    let audioBlob;
    try {
      audioBlob = await convertAudio(blob, props.audioRecordFormat);
    } catch (error) {
      URL.revokeObjectURL(audioUrl);
      if (idAtStart !== cancelGeneration.value) {
        isRecording.value = false;
        emit('finishRecord', null);
        return;
      }
      fail(error, blob);
      return;
    }

    if (idAtStart !== cancelGeneration.value) {
      URL.revokeObjectURL(audioUrl);
      isRecording.value = false;
      emit('finishRecord', null);
      return;
    }

    const ext = props.audioRecordFormat === 'audio/ogg' ? 'ogg' : props.audioRecordFormat === 'audio/wav' ? 'wav' : 'mp3';
    const fileName = `${getUuid()}.${ext}`;
    const file = new File([audioBlob], fileName, {
      type: props.audioRecordFormat,
    });
    try {
      if (wavesurfer.value) {
        wavesurfer.value.load(audioUrl);
      }
    } catch {
      /* ignore load error on unmount */
    }
    emit('finishRecord', {
      name: file.name,
      type: file.type,
      size: file.size,
      file,
    });
    hasRecording.value = true;
    isRecording.value = false;
  });

  record.value.on('record-progress', time => {
    emit('recorderProgressChanged', formatTimeProgress(time));
  });
};

const stopRecording = () => {
  if (record.value && (isRecording.value || record.value.isRecording?.())) {
    try {
      record.value.stopRecording();
    } catch {
      /* já parado */
    }
    isRecording.value = false;
  }
};

const cancelRecording = () => {
  stopRecording();
  cancelGeneration.value += 1;
};

const startRecording = async () => {
  cancelGeneration.value = 0;
  try {
    // Libera o prompt do browser antes do plugin (melhor no iframe).
    await ensureMicrophoneAccess();
    // RecordPlugin só aceita { deviceId } — constraints já validadas acima.
    await record.value.startRecording();
    isRecording.value = true;
  } catch (error) {
    fail(error);
  }
};

const playPause = () => {
  if (hasRecording.value) {
    wavesurfer.value.playPause();
    isPlaying.value = !isPlaying.value;
  }
};

onMounted(async () => {
  initWaveSurfer();
  await startRecording();
});

onUnmounted(() => {
  if (wavesurfer.value) {
    try {
      wavesurfer.value.destroy();
    } catch {
      /* ignore abort on unmount */
    }
  }
});

defineExpose({ playPause, stopRecording, cancelRecording, record });
</script>

<template>
  <div ref="waveformContainer" class="w-full p-1" />
</template>
